import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, BuildingOfficeIcon, PhoneIcon, UserCircleIcon, PencilIcon, UserPlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { markCompanyContacted, updateCompanyStatusHistory } from '../../../services/firebase';
import ContactSelector from '../../features/dashboard/jobs/ContactSelector';
import FastTrackContactModal from '../../features/dashboard/jobs/FastTrackContactModal';

// Activity Item Component
function ActivityItem({ activity, onTagContactLead, onMarkContacted, onEditContactLead, onDeleteActivity }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Check if timestamp is a Firestore timestamp
      if (timestamp.toDate) {
        return format(timestamp.toDate(), 'MMM d, yyyy, h:mm a');
      }
      
      // Check if timestamp is a string (ISO format)
      if (typeof timestamp === 'string') {
        return format(new Date(timestamp), 'MMM d, yyyy, h:mm a');
      }
      
      return 'Invalid date';
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Determine icon based on activity type
  let Icon = BuildingOfficeIcon;
  if (activity.status === 'admin_contacted_company') {
    Icon = PhoneIcon;
  } else if (activity.status === 'contact_lead_assigned' || activity.status === 'contact_lead_changed') {
    Icon = UserCircleIcon;
  }

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (onDeleteActivity) {
      onDeleteActivity(activity);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="relative pb-8">
      {/* Timeline connector line */}
      <div className="absolute left-5 top-5 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
      
      <div className="relative flex items-start">
        <div className="flex items-center justify-center">
          <div className="relative z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            {activity.user?.photoURL ? (
              <img 
                src={activity.user.photoURL} 
                alt={activity.user.displayName === null ? 'Admin' : 
                  (typeof activity.user.displayName === 'object' ? 
                    (activity.user.displayName.text || 'Admin') : 
                    (activity.user.displayName || 'Admin'))} 
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : activity.companyLogo ? (
              <img 
                src={activity.companyLogo} 
                alt={activity.companyName || 'Company'} 
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <Icon className="h-5 w-5 text-gray-500 dark:text-gray-400" aria-hidden="true" />
            )}
          </div>
        </div>
        <div className="ml-4 flex-1">
          <div className="flex justify-between">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">{activity.title}</h3>
            <div className="flex items-center">
              <time dateTime={activity.timestamp} className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                {formatTimestamp(activity.timestamp)}
              </time>
              <button 
                onClick={handleDelete}
                className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors duration-150"
                title="Remove this activity"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {activity.description}
          </p>
          
          {/* Action buttons for different statuses */}
          {activity.status === 'introduction-requested' && activity.showContactButton && (
            <button
              onClick={() => onTagContactLead(activity.companyId, activity.companyName)}
              className="mt-2 inline-flex items-center rounded-md bg-white dark:bg-gray-700 px-2.5 py-1.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 shadow-sm ring-1 ring-inset ring-indigo-300 dark:ring-indigo-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Tag Contact Lead
            </button>
          )}
          
          {(activity.status === 'contact_lead_assigned' || activity.status === 'contact_lead_changed') && (
            <div className="mt-2 flex space-x-2">
              {activity.showContactButton && (
                <button
                  onClick={() => onMarkContacted(activity.companyId, activity.companyName, activity.assignedAdmin)}
                  className="inline-flex items-center rounded-md bg-white dark:bg-gray-700 px-2.5 py-1.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 shadow-sm ring-1 ring-inset ring-indigo-300 dark:ring-indigo-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Mark as Contacted
                </button>
              )}
              <button
                onClick={() => onEditContactLead(activity.companyId, activity.companyName, activity.assignedAdmin?.id)}
                className="inline-flex items-center rounded-md bg-white dark:bg-gray-700 px-2.5 py-1.5 text-sm font-semibold text-gray-600 dark:text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <PencilIcon className="mr-1 h-4 w-4" />
                Edit Lead
              </button>
            </div>
          )}

          {/* Delete confirmation dialog */}
          {showDeleteConfirm && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-600 dark:text-red-400">Are you sure you want to delete this activity? This cannot be undone.</p>
              <div className="mt-2 flex space-x-2">
                <button
                  onClick={confirmDelete}
                  className="inline-flex items-center rounded-md bg-red-100 dark:bg-red-900 px-2.5 py-1.5 text-xs font-semibold text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800"
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="inline-flex items-center rounded-md bg-white dark:bg-gray-800 px-2.5 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CandidateActivityTab({ user }) {
  const [activities, setActivities] = useState([]);
  const [showTagLeadModal, setShowTagLeadModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactNotes, setContactNotes] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showAddContactModal, setShowAddContactModal] = useState(false);

  // Admin options for the dropdown
  const adminOptions = [
    { id: 'grey@justburnt.com', name: 'Grey', uid: 'grey123' },
    { id: 'arjan@justburnt.com', name: 'Arjan', uid: 'arjan123' },
    { id: 'louie@justburnt.com', name: 'Louie', uid: 'louie123' }
  ];

  // Process user data to populate activities
  React.useEffect(() => {
    if (!user) return;

    const activityItems = [];
    
    // Process company status history
    const companyStatusHistory = user.companyStatusHistory || {};
    const candidateName = user.displayName === null ? 'Candidate' : 
      (typeof user.displayName === 'object' ? 
        user.displayName.text : 
        user.displayName) || 'Candidate';
    
    Object.entries(companyStatusHistory).forEach(([companyId, historyEvents]) => {
      if (!Array.isArray(historyEvents)) return;
      
      historyEvents.forEach((event, eventIndex) => {
        if (event.status === 'introduction-requested') {
          // Check if there's a contact lead assigned to this company
          const hasContactLead = historyEvents.some(e => 
            e.status === 'contact_lead_assigned' || e.status === 'contact_lead_changed');
          
          // Check if this company has been contacted already
          const isContactedAlready = 
            (user.contactOutreach && 
             user.contactOutreach[companyId] && 
             user.contactOutreach[companyId].status === 'contacted') ||
            (user.companyStatuses && 
             user.companyStatuses[companyId] === 'contact-established');
          
          const companyName = user._companyDetails?.[companyId]?.name || companyId;
          const companyLogo = user._companyDetails?.[companyId]?.logo || null;
          
          activityItems.push({
            status: event.status,
            title: `Intro Request: ${companyName}`,
            description: `${candidateName} requested an introduction to this company.`,
            timestamp: event.timestamp,
            companyId,
            companyName,
            companyLogo,
            showContactButton: !isContactedAlready && !hasContactLead,
            eventIndex
          });
        } 
        else if (event.status === 'contact_lead_assigned' || event.status === 'contact_lead_changed') {
          // Check if this company has been contacted already
          const isContactedAlready = 
            (user.contactOutreach && 
             user.contactOutreach[companyId] && 
             user.contactOutreach[companyId].status === 'contacted') ||
            (user.companyStatuses && 
             user.companyStatuses[companyId] === 'contact-established');
          
          const companyName = user._companyDetails?.[companyId]?.name || companyId;
          const companyLogo = user._companyDetails?.[companyId]?.logo || null;
          
          // Prefer the specifically assigned admin name if available
          const adminName = event.assignedAdminName || 
                            user._adminDetails?.[event.adminUid]?.firstName || 
                            adminOptions.find(a => a.id === event.assignedAdminEmail)?.name || 
                            'Admin';
          
          const actionText = event.status === 'contact_lead_assigned' ? 'assigned as' : 'changed to';
          
          activityItems.push({
            status: event.status,
            title: `Contact Lead ${event.status === 'contact_lead_assigned' ? 'Assigned' : 'Changed'}: ${companyName}`,
            description: `${adminName} ${actionText} contact lead for ${candidateName}.`,
            timestamp: event.timestamp,
            user: { 
              displayName: adminName,
              photoURL: user._adminDetails?.[event.adminUid]?.photoURL
            },
            companyId,
            companyName,
            companyLogo,
            assignedAdmin: {
              id: event.assignedAdminEmail || event.adminUid,
              name: adminName
            },
            showContactButton: !isContactedAlready,
            eventIndex
          });
        }
        else if (event.status === 'admin_contacted_company') {
          const companyName = user._companyDetails?.[companyId]?.name || companyId;
          const companyLogo = user._companyDetails?.[companyId]?.logo || null;
          
          // Get admin info
          const adminName = user._adminDetails?.[event.adminUid]?.firstName || 
                            adminOptions.find(a => a.id === event.updatedByAdminEmail)?.name || 
                            'Admin';
          
          // Include contact name if available
          const contactName = event.contactName || (event.contactId ? 'a contact' : '');
          
          let description = `${adminName} initiated contact with ${companyName}`;
          if (contactName) {
            description += ` through ${contactName}`;
          }
          description += ` for ${candidateName}.`;
          
          // If there are notes, add them
          if (event.notes) {
            description += ` Notes: ${event.notes}`;
          }
          
          activityItems.push({
            status: event.status,
            title: `Contact Initiated: ${companyName}`,
            description,
            timestamp: event.timestamp,
            user: { 
              displayName: adminName,
              photoURL: user._adminDetails?.[event.adminUid]?.photoURL
            },
            companyId,
            companyName,
            companyLogo,
            contactId: event.contactId,
            contactName: event.contactName,
            eventIndex
          });
        }
      });
    });
    
    // Sort activities by timestamp (oldest first)
    activityItems.sort((a, b) => {
      const timeA = a.timestamp?.toDate?.() || new Date(a.timestamp || 0);
      const timeB = b.timestamp?.toDate?.() || new Date(b.timestamp || 0);
      return timeA - timeB;
    });
    
    setActivities(activityItems);
  }, [user]);

  // Handle tagging a contact lead
  const handleTagContactLead = (companyId, companyName) => {
    setSelectedCompany({ id: companyId, name: companyName });
    setSelectedAdmin('');
    setIsEditingLead(false);
    setShowTagLeadModal(true);
  };

  // Handle editing a contact lead
  const handleEditContactLead = (companyId, companyName, currentAdminId) => {
    setSelectedCompany({ id: companyId, name: companyName });
    setSelectedAdmin(currentAdminId || '');
    setIsEditingLead(true);
    setShowTagLeadModal(true);
  };

  // Handle mark as contacted
  const handleMarkContacted = (companyId, companyName, assignedAdmin = null) => {
    setSelectedCompany({ 
      id: companyId, 
      name: companyName,
      assignedAdmin
    });
    setContactNotes('');
    setShowContactModal(true);
  };

  // Handle contact select
  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
  };

  // Handle showing new contact modal
  const handleAddNewContact = () => {
    setShowAddContactModal(true);
  };

  // Handle contact added
  const handleContactAdded = (contact) => {
    setShowAddContactModal(false);
    if (contact) {
      setSelectedContact({
        id: contact.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        phone: contact.phone,
        company: contact.company
      });
    }
  };

  // Handle deleting an activity
  const handleDeleteActivity = async (activity) => {
    if (!user || !activity) return;
    
    try {
      setIsSubmitting(true);
      
      // Get the current history for this company
      const currentHistory = user.companyStatusHistory?.[activity.companyId] || [];
      
      if (!Array.isArray(currentHistory) || currentHistory.length === 0) {
        toast.error("Cannot find history to delete");
        return;
      }
      
      // Filter out the event we want to delete
      const updatedHistory = currentHistory.filter((_, index) => index !== activity.eventIndex);
      
      // Update the company status history with the filtered array
      const result = await updateCompanyStatusHistory(
        user.id,
        activity.companyId,
        "history_update", // Use a generic status type for this operation
        { fullHistoryUpdate: updatedHistory }
      );
      
      if (result.success) {
        toast.success(`Removed activity: ${activity.title}`);
        
        // Remove the activity locally
        setActivities(activities.filter(a => 
          !(a.companyId === activity.companyId && a.eventIndex === activity.eventIndex)
        ));
        
        // Trigger parent component to refresh data
        if (typeof window !== 'undefined') {
          const refreshEvent = new CustomEvent('refresh-candidate-data', { 
            detail: { userId: user.id } 
          });
          window.dispatchEvent(refreshEvent);
        }
      } else {
        toast.error(`Failed to remove activity: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast.error('Failed to remove activity');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit the contact lead assignment
  const handleSubmitTagLead = async () => {
    if (!selectedCompany || !selectedAdmin) {
      toast.error('Please select an admin');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Find the admin object
      const admin = adminOptions.find(a => a.id === selectedAdmin);
      
      // Add a new entry to companyStatusHistory with appropriate status
      const status = isEditingLead ? 'contact_lead_changed' : 'contact_lead_assigned';
      
      const result = await updateCompanyStatusHistory(
        user.id, 
        selectedCompany.id, 
        status,
        {
          assignedAdminUid: admin.uid,
          assignedAdminEmail: admin.id,
          assignedAdminName: admin.name
        }
      );
      
      if (result.success) {
        const action = isEditingLead ? 'Updated' : 'Tagged';
        toast.success(`${action} ${admin.name} as contact lead for ${selectedCompany.name}`);
        setShowTagLeadModal(false);
        
        // Trigger parent component to refresh data
        if (typeof window !== 'undefined') {
          const refreshEvent = new CustomEvent('refresh-candidate-data', { 
            detail: { userId: user.id } 
          });
          window.dispatchEvent(refreshEvent);
        }
      } else {
        toast.error(`Failed to ${isEditingLead ? 'update' : 'tag'} contact lead: ${result.error}`);
      }
    } catch (error) {
      console.error(`Error ${isEditingLead ? 'updating' : 'tagging'} contact lead:`, error);
      toast.error(`Failed to ${isEditingLead ? 'update' : 'tag'} contact lead`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitContact = async () => {
    if (!selectedCompany) return;
    
    setIsSubmitting(true);
    try {
      // If there's an assigned admin, pass that info to the contact function
      let adminInfo = null;
      if (selectedCompany.assignedAdmin) {
        adminInfo = {
          adminUid: selectedCompany.assignedAdmin.id,
          adminEmail: selectedCompany.assignedAdmin.email
        };
      }
      
      // Add contact info to the request
      const contactInfo = selectedContact ? {
        contactId: selectedContact.id,
        contactName: `${selectedContact.firstName || ''} ${selectedContact.lastName || ''}`.trim(),
        contactEmail: selectedContact.email
      } : null;
      
      // Add additional metadata about the contact in the notes field
      const notes = contactInfo ? 
        `${contactNotes ? contactNotes + ' ' : ''}Contact: ${contactInfo.contactName}${contactInfo.contactEmail ? ` (${contactInfo.contactEmail})` : ''}` :
        contactNotes;
      
      const result = await markCompanyContacted(
        user.id, 
        selectedCompany.id, 
        selectedCompany.name, 
        notes,
        adminInfo,
        contactInfo
      );
      
      if (result.success) {
        toast.success(`Marked ${selectedCompany.name} as contacted` + (contactInfo ? ` through ${contactInfo.contactName}` : ''));
        setShowContactModal(false);
        setContactNotes('');
        setSelectedContact(null);
        
        // Trigger parent component to refresh data
        if (typeof window !== 'undefined') {
          // Use a custom event to signal the parent component to refresh
          const refreshEvent = new CustomEvent('refresh-candidate-data', { 
            detail: { userId: user.id } 
          });
          window.dispatchEvent(refreshEvent);
        }
      } else {
        toast.error(`Failed to mark company as contacted: ${result.error}`);
      }
    } catch (error) {
      console.error('Error marking company as contacted:', error);
      toast.error('Failed to mark company as contacted');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return <div>No user data available</div>;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Company Interaction History</h3>
      
      {activities.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">No company interactions found.</p>
      ) : (
        <div className="flow-root mt-4">
          <ul className="-mb-8">
            {activities.map((activity, index) => (
              <li key={`${activity.companyId}-${activity.status}-${index}`}>
                <ActivityItem 
                  activity={activity} 
                  onTagContactLead={handleTagContactLead}
                  onMarkContacted={handleMarkContacted}
                  onEditContactLead={handleEditContactLead}
                  onDeleteActivity={handleDeleteActivity}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tag Contact Lead Modal */}
      <Transition.Root show={showTagLeadModal} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={() => setShowTagLeadModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="absolute right-0 top-0 pr-4 pt-4">
                      <button
                        type="button"
                        className="rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        onClick={() => setShowTagLeadModal(false)}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        {isEditingLead ? 'Change' : 'Tag'} Contact Lead for {selectedCompany?.name}
                      </Dialog.Title>
                      <div className="mt-2">
                                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                            {isEditingLead 
                              ? `Update the admin who will be responsible for contacting ${user.displayName === null ? 'this candidate' : 
                                  (typeof user.displayName === 'object' ? 
                                    user.displayName.text || 'this candidate' : 
                                    user.displayName || 'this candidate')}.`
                              : `Select an admin who will be responsible for contacting ${user.displayName === null ? 'this candidate' : 
                                  (typeof user.displayName === 'object' ? 
                                    user.displayName.text || 'this candidate' : 
                                    user.displayName || 'this candidate')}.`}
                          </p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label htmlFor="admin-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Admin
                      </label>
                      <select
                        id="admin-select"
                        name="admin-select"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-gray-700 dark:text-white"
                        value={selectedAdmin}
                        onChange={(e) => setSelectedAdmin(e.target.value)}
                      >
                        <option value="">Select an admin</option>
                        {adminOptions.map((admin) => (
                          <option key={admin.id} value={admin.id}>
                            {admin.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleSubmitTagLead}
                      disabled={isSubmitting || !selectedAdmin}
                    >
                      {isSubmitting ? 'Saving...' : (isEditingLead ? 'Update Lead' : 'Save')}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0"
                      onClick={() => setShowTagLeadModal(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Mark as Contacted Modal */}
      <Transition.Root show={showContactModal} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={() => setShowContactModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="absolute right-0 top-0 pr-4 pt-4">
                      <button
                        type="button"
                        className="rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        onClick={() => setShowContactModal(false)}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        Mark {selectedCompany?.name} as Contacted
                      </Dialog.Title>
                      <div className="mt-2">
                                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                            Record that you've reached out to this company on behalf of {user.displayName === null ? 'this candidate' : 
                              (typeof user.displayName === 'object' ? 
                                (user.displayName.text || 'this candidate') : 
                                user.displayName || 'this candidate')}.
                          </p>
                        {selectedCompany?.assignedAdmin && (
                          <p className="mt-1 text-sm font-medium text-indigo-600 dark:text-indigo-400">
                            Contact Lead: {selectedCompany.assignedAdmin.name}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Contact selection */}
                    <div className="mt-4">
                      <div className="flex justify-between items-center">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Contact</h4>
                        <button
                          type="button"
                          onClick={handleAddNewContact}
                          className="inline-flex items-center rounded-md bg-indigo-50 dark:bg-indigo-900 px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-800"
                        >
                          <UserPlusIcon className="h-4 w-4 mr-1" />
                          Add New
                        </button>
                      </div>
                      <div className="mt-2">
                        <ContactSelector
                          onSelectContact={handleContactSelect}
                          userId={user.id}
                          companyId={selectedCompany?.id}
                        />
                      </div>
                      {selectedContact && (
                        <div className="mt-2 p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-md">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedContact.firstName} {selectedContact.lastName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {selectedContact.email} {selectedContact.phone ? ` • ${selectedContact.phone}` : ''}
                          </p>
                          {selectedContact.company && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {selectedContact.company}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4">
                      <label htmlFor="contact-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Contact Notes (optional)
                      </label>
                      <div className="mt-1">
                        <textarea
                          id="contact-notes"
                          name="contact-notes"
                          rows={3}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          placeholder="Any details about your outreach..."
                          value={contactNotes}
                          onChange={(e) => setContactNotes(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                      onClick={handleSubmitContact}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0"
                      onClick={() => setShowContactModal(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Add Contact Modal */}
      {showAddContactModal && (
        <FastTrackContactModal
          companyId={selectedCompany?.id}
          onClose={() => setShowAddContactModal(false)}
          onContactAdded={handleContactAdded}
        />
      )}
    </div>
  );
} 